/* ============================================================
   Midnight Atelier — shared lead-form handler
   Used by sections/ma-contact.liquid (site contact form) and
   sections/ma-product.liquid (product quote modal form). Each
   section renders a plain `<form data-ma-lead-form>` (no Liquid
   `form` tag) and passes its own config — CRM endpoint, API key,
   redirect URL, button labels, validation copy — in as data-*
   attributes, because this file is a static asset and never runs
   through the Liquid engine.

   Flow: preventDefault -> validate -> POST JSON to the CRM ->
   redirect to the thank-you page on success, or show an inline
   error and re-enable the form on failure.

   TRANSPORT: navigator.sendBeacon cannot attach custom headers
   (it only supports a small allow-list of simple content-types
   and no header list at all), so it can never carry the
   x-goober-key header — a beacon POST to this endpoint would
   arrive unauthenticated and be dropped by the CRM. fetch() with
   keepalive:true is used instead: it supports arbitrary headers
   AND, like sendBeacon, is allowed to keep running after the page
   starts navigating away. XMLHttpRequest (also header-capable) is
   the fallback for browsers without fetch/keepalive support.
   Do NOT "simplify" this back to sendBeacon — it would silently
   drop the CRM key on every single lead.

   Vanilla ES5, section-scoped (no globals besides this IIFE),
   safe if a section renders more than once on the same page.
   ============================================================ */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* 90 days in ms — matches the goob_attr TTL in the Goober platform
     tracking snippet in layout/theme.liquid, so attribution carried
     on a lead is only used while that snippet would still consider
     it fresh. */
  var ATTR_MAX_AGE_MS = 7776e6;

  /* Same sessionStorage key + object shape as the "Lead form
     conversion" block in layout/theme.liquid. That block only
     stamps this token for a genuine Shopify contact[] form post (it
     checks name="form_type" value "contact"), which these plain
     forms no longer submit — so this file stamps the token itself,
     matching the shape exactly, so the Google Ads conversion fired
     on the thank-you page still counts these leads. */
  var CONVERSION_KEY = 'ww_lead_pending';

  var canFetch = !!(window.fetch && window.Request && ('keepalive' in window.Request.prototype));

  /* ---------------------------------------------------------
     Attribution (gclid / utm_*)
     Read-only mirror of the attr() helper inside the Goober
     platform tracking snippet in layout/theme.liquid — that
     snippet is the one that WRITES goob_attr; this file only
     ever reads it back.
     --------------------------------------------------------- */
  function readAttr() {
    try {
      var raw = localStorage.getItem('goob_attr');
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed.ts === 'number' && (Date.now() - parsed.ts) < ATTR_MAX_AGE_MS) {
        return parsed;
      }
    } catch (err) {}
    return null;
  }

  function stampConversionToken(email, phone) {
    try {
      sessionStorage.setItem(CONVERSION_KEY, JSON.stringify({
        id: 'lead-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
        ts: Date.now(),
        from: location.pathname,
        email: email || '',
        phone: phone || ''
      }));
    } catch (err) {}
  }

  /* ---------------------------------------------------------
     Field collection
     --------------------------------------------------------- */
  function fieldValue(form, role) {
    var el = form.querySelector('[data-ma-role="' + role + '"]');
    return el ? (el.value || '').trim() : '';
  }

  /* Every field that isn't name/email/phone/message (e.g. Suburb,
     Product) is sent under `fields`, keyed by its VISIBLE label text
     so a client edit to the label setting flows straight through
     without a code change. A field with no visible label of its own
     (the quote form's hidden Product input) carries a
     data-ma-field-label override instead — the one deliberate
     exception to "derive the key from the rendered DOM". */
  function fieldLabel(el) {
    var override = el.getAttribute('data-ma-field-label');
    if (override) return override;
    if (!el.id || !el.form) return '';
    var label = el.form.querySelector('label[for="' + el.id + '"]');
    if (!label) return '';
    var text = label.textContent || '';
    text = text.replace(/\*/g, '').replace(/\(optional\)/ig, '').replace(/\s+/g, ' ').trim();
    return text;
  }

  function collectExtraFields(form) {
    var out = {};
    var els = form.querySelectorAll('[data-ma-extra-field]');
    for (var i = 0; i < els.length; i++) {
      var key = fieldLabel(els[i]);
      if (!key) continue;
      var value = (els[i].value || '').trim();
      if (value === '') continue;
      out[key] = value;
    }
    return out;
  }

  function buildPayload(form) {
    return {
      name: fieldValue(form, 'name'),
      email: fieldValue(form, 'email'),
      phone: fieldValue(form, 'phone'),
      message: fieldValue(form, 'message'),
      fields: collectExtraFields(form),
      source: 'website_form:' + location.pathname,
      meta: {
        page: location.pathname,
        captured: 'ma_lead_form',
        attr: readAttr()
      }
    };
  }

  /* ---------------------------------------------------------
     Validation
     Mirrors the aria-describedby wiring the product section
     already uses for its measurement fields: the input points at
     its error element via aria-describedby, the error element
     starts empty, and this file only ever toggles its text — no
     separate hidden/shown state to keep in sync.
     --------------------------------------------------------- */
  function errorElFor(input) {
    var id = input.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  function setFieldError(input, message) {
    input.setAttribute('aria-invalid', 'true');
    var el = errorElFor(input);
    if (el) el.textContent = message;
    var wrapper = input.closest('.ma-field, .ma-contact__field, .wwlp-hero-quote-form__field');
    if (wrapper) wrapper.classList.add('has-error');
  }

  function clearFieldError(input) {
    input.removeAttribute('aria-invalid');
    var el = errorElFor(input);
    if (el) el.textContent = '';
    var wrapper = input.closest('.ma-field, .ma-contact__field, .wwlp-hero-quote-form__field');
    if (wrapper) wrapper.classList.remove('has-error');
  }

  function validate(form) {
    var valid = true;
    var firstInvalid = null;
    var requiredMsg = form.getAttribute('data-ma-msg-required') || 'This field is required.';
    var emailMsg = form.getAttribute('data-ma-msg-email') || 'Enter a valid email address.';

    var required = form.querySelectorAll('[data-ma-required]');
    for (var i = 0; i < required.length; i++) {
      var input = required[i];
      if ((input.value || '').trim() === '') {
        setFieldError(input, requiredMsg);
        valid = false;
        if (!firstInvalid) firstInvalid = input;
      } else {
        clearFieldError(input);
      }
    }

    /* Email format is checked unconditionally (both forms always mark
       email as required, but the format rule is a separate concern
       from emptiness). */
    var emailInput = form.querySelector('[data-ma-role="email"]');
    if (emailInput) {
      var emailValue = (emailInput.value || '').trim();
      if (emailValue !== '' && !EMAIL_RE.test(emailValue)) {
        setFieldError(emailInput, emailMsg);
        valid = false;
        if (!firstInvalid) firstInvalid = emailInput;
      }
    }

    return { valid: valid, firstInvalid: firstInvalid };
  }

  /* Clears a field's own error as soon as it looks valid again,
     rather than waiting for the next submit attempt — same idea as
     the swatch/measurement fields elsewhere in ma-product.liquid. */
  function attachLiveClear(form) {
    var fields = form.querySelectorAll('[data-ma-required], [data-ma-role="email"]');
    for (var i = 0; i < fields.length; i++) {
      attachLiveClearOne(fields[i]);
    }
  }

  function attachLiveClearOne(input) {
    input.addEventListener('input', function () {
      var value = (input.value || '').trim();
      if (value === '') return;
      if (input.getAttribute('data-ma-role') === 'email' && !EMAIL_RE.test(value)) return;
      clearFieldError(input);
    });
  }

  /* ---------------------------------------------------------
     Transport — see the file banner above for why this is
     fetch-first with an XMLHttpRequest fallback, and never
     navigator.sendBeacon.
     --------------------------------------------------------- */
  function sendLead(url, key, body, done) {
    var json = JSON.stringify(body);
    var settled = false;

    function finish(ok) {
      if (settled) return;
      settled = true;
      done(ok);
    }

    if (canFetch) {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-goober-key': key },
        body: json,
        keepalive: true
      }).then(function (response) {
        finish(!!response && response.ok);
      }).catch(function () {
        finish(false);
      });
      return;
    }

    try {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('x-goober-key', key);
      xhr.onload = function () {
        finish(xhr.status >= 200 && xhr.status < 300);
      };
      xhr.onerror = function () {
        finish(false);
      };
      xhr.send(json);
    } catch (err) {
      finish(false);
    }
  }

  /* ---------------------------------------------------------
     Per-form wiring
     --------------------------------------------------------- */
  function initForm(form) {
    if (form.getAttribute('data-ma-ready') === 'true') return;
    form.setAttribute('data-ma-ready', 'true');

    var submitBtn = form.querySelector('[data-ma-submit]');
    var labelEl = submitBtn ? (submitBtn.querySelector('[data-ma-submit-label]') || submitBtn) : null;
    var originalLabel = labelEl ? labelEl.textContent : '';
    var sendingLabel = form.getAttribute('data-ma-sending-label') || 'Sending…';
    var successEl = form.querySelector('[data-ma-form-success]');
    var errorEl = form.querySelector('[data-ma-form-error]');
    var endpoint = form.getAttribute('data-ma-endpoint');
    var key = form.getAttribute('data-ma-key');
    var redirectUrl = form.getAttribute('data-ma-redirect') || '/pages/thank-you';
    var submitting = false;

    attachLiveClear(form);

    function hideMessages() {
      if (successEl) successEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
    }

    function showError() {
      if (!errorEl) return;
      errorEl.hidden = false;
      if (typeof errorEl.focus === 'function') errorEl.focus();
    }

    function setBusy(busy) {
      if (submitBtn) {
        submitBtn.disabled = busy;
        if (busy) {
          submitBtn.setAttribute('aria-busy', 'true');
        } else {
          submitBtn.removeAttribute('aria-busy');
        }
      }
      if (labelEl) labelEl.textContent = busy ? sendingLabel : originalLabel;
    }

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      if (submitting) return;

      hideMessages();

      var result = validate(form);
      if (!result.valid) {
        if (result.firstInvalid && typeof result.firstInvalid.focus === 'function') {
          result.firstInvalid.focus();
        }
        return;
      }

      if (!endpoint || !key) {
        showError();
        return;
      }

      submitting = true;
      setBusy(true);

      var body = buildPayload(form);

      sendLead(endpoint, key, body, function (ok) {
        submitting = false;
        setBusy(false);

        if (ok) {
          stampConversionToken(body.email, body.phone);
          if (successEl) successEl.hidden = false;
          window.location.assign(redirectUrl);
        } else {
          showError();
        }
      });
    });
  }

  function initAll(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var forms = scope.querySelectorAll('[data-ma-lead-form]');
    for (var i = 0; i < forms.length; i++) {
      initForm(forms[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: a section re-render replaces the DOM node
     wholesale, so the freshly-inserted form needs its own init. */
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
